import { nextTick, ref, type Ref } from "vue";
import type { FormContext } from "vee-validate";
import { ApolloError, FetchResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { localStorageGetUserId } from "@/common/localStorageUtil";
import { DeliveryRequireMoney } from "@/common/constant";
import {
  CreateMemberDocument,
  CreateMemberMutation,
  CreateMemberMutationVariables,
  DeliveryRequestUpdateInput,
  MemberCreateInput,
  MemberUpdateInput,
  OperationInput,
  UpdateDeliveryRequestDocument,
  UpdateDeliveryRequestMutation,
  UpdateDeliveryRequestMutationVariables,
  UpdateMemberDocument,
  UpdateMemberMutation,
  UpdateMemberMutationVariables,
} from "@/generated/graphql";

type DeliveryStartMinit = { label: string; deliveryStartMinit: number };

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliveryRequestEditFormParams {
  editPage: Ref<boolean>;
  confirmPage: Ref<boolean>;
  processFlag: Ref<number>;
  overlay: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editDeliveryRequest: Ref<DeliveryRequestUpdateInput | null>;
  memberNotesBefore: Ref<string>;
  deliveryNotesBefore: Ref<string>;
  messageMemberNotes: Ref<string>;
  messageDeliveryNotes: Ref<string>;
  paymentRequiredFlg: Ref<boolean>;
  paymentList: Ref<
    | Array<
      | {
        __typename?: "ChainPaymentMethod" | undefined;
        chain_code: string;
        created_date: string;
        created_user_id: string;
        customer_code: string;
        payment_amount_required: string;
        payment_method: string;
        sort_order: number;
        updated_date?: string | null | undefined;
        updated_user_id?: string | null | undefined;
      }
      | null
      | undefined
    >
    | null
    | undefined
  >;
  dummy_payment_amount: Ref<string>;
  house_card_befor: Ref<string>;
  house_card_change: Ref<boolean>;
  irregularDeliveryStartTime: Ref<string>;
  irregularDeliveryEndTime: Ref<string>;
  deliveryId_0_Flg: Ref<boolean>;
  pre_double_deli_oricon_flg: Ref<number | null | undefined>;
  pre_charter_deli_oricon_flg: Ref<number | null | undefined>;
  isDoubleDeli: Ref<boolean>;
  isCharterDeli: Ref<boolean>;
  doubleDeliOriconCount: Ref<number | null | undefined>;
  charterDeliOriconCount: Ref<number | null | undefined>;
  acceptableEndTime: Ref<string>;
  overDeliveryEndTime: Ref<boolean>;
  /** 金額のみ編集可（本部の締め日期間内編集）の場合に true。 */
  isDisabled: Ref<boolean>;
  wordList: Record<string, string>;
  openListPage: () => void;
  returnToListPage: () => Promise<void>;
  getDoubleDeliOriconCount: () => number | null | undefined;
  getCharterDeliOriconCount: () => number | null | undefined;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestEditForm = ({
  editPage,
  confirmPage,
  processFlag,
  overlay,
  errdialog,
  errMessage,
  editDeliveryRequest,
  memberNotesBefore,
  deliveryNotesBefore,
  messageMemberNotes,
  messageDeliveryNotes,
  paymentRequiredFlg,
  paymentList,
  dummy_payment_amount,
  house_card_befor,
  house_card_change,
  irregularDeliveryStartTime,
  irregularDeliveryEndTime,
  deliveryId_0_Flg,
  pre_double_deli_oricon_flg,
  pre_charter_deli_oricon_flg,
  isDoubleDeli,
  isCharterDeli,
  doubleDeliOriconCount,
  charterDeliOriconCount,
  acceptableEndTime,
  overDeliveryEndTime,
  isDisabled,
  wordList,
  openListPage,
  returnToListPage,
  getDoubleDeliOriconCount,
  getCharterDeliOriconCount,
}: UseDeliveryRequestEditFormParams) => {
  const editFormRef = ref<FormContext | null>(null);

  const memberCreateInput = ref<MemberCreateInput>({
    address1: "",
    address2: "",
    address3: "",
    chain_code: "",
    customer_code: "",
    name1: "",
    name2: "",
    name_kana1: "",
    name_kana2: "",
    notes: "",
    phone: "",
    shop_code: "",
    zip_code: "",
    house_card: "",
  });

  const memberUpdateInput = ref<MemberUpdateInput>({
    customer_code: "",
    chain_code: "",
    address1: "",
    address2: "",
    address3: "",
    member_id: "",
    name1: "",
    name2: "",
    name_kana1: "",
    name_kana2: "",
    search_kana: "",
    notes: "",
    phone: "",
    shop_code: "",
    zip_code: "",
    house_card: "",
  });

  const operationInput = ref<OperationInput>({
    area_code: "10",
    detail: "操作詳細",
    display_category: "画面名",
    operation: "操作",
    user_id: localStorageGetUserId(),
  });

  const updateDeliveryRequestMutation = ref<
    UpdateDeliveryRequestMutation | null | undefined
  >(null);
  const createMemberMutation = ref<CreateMemberMutation | null | undefined>(
    null
  );
  const updateMemberMutation = ref<UpdateMemberMutation | null | undefined>(
    null
  );

  const validateEditForm = async (enabled: boolean): Promise<void> => {
    if (!enabled || !editFormRef.value) {
      return;
    }
    await editFormRef.value.validate({ mode: "validated-only" });
  };

  const onChangePaymentMethod = async (
    item: string | undefined
  ): Promise<void> => {
    try {
      if (item === null || item === undefined) {
        return;
      }
      const _paymentList = paymentList?.value?.find(
        (element) => element?.payment_method === item
      );
      if (!_paymentList) {
        return;
      }
      if (
        _paymentList.payment_amount_required ===
        DeliveryRequireMoney.money_required
      ) {
        paymentRequiredFlg.value = true;
      } else {
        paymentRequiredFlg.value = false;
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = "支払方法の取得に失敗しました。";
    }
  };

  const commmaMoney = async (value: string): Promise<void> => {
    dummy_payment_amount.value = String(value).replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1,"
    );
  };

  const removeComma = async (value: string): Promise<string> => {
    return String(value).replace(/,/g, "").replace(/¥/g, "");
  };

  const onFocus = async (value: string): Promise<void> => {
    dummy_payment_amount.value = await removeComma(value);
  };

  const onBlur = async (value: string): Promise<void> => {
    value = await removeComma(value);

    if (value) {
      const regex = new RegExp("^[0-9]+$");
      if (!regex.test(value)) {
        errdialog.value = true;
        errMessage.value = "数値およびカンマのみ入力してください";
        return;
      }
    }

    await commmaMoney(value);
  };

  const onChangeHouseCard = async (): Promise<void> => {
    if (!editDeliveryRequest.value) {
      return;
    }
    if (editDeliveryRequest.value.house_card !== house_card_befor.value) {
      house_card_change.value = true;
      if (editDeliveryRequest.value.house_card == "") {
        overlay.value = false;
        errdialog.value = true;
        errMessage.value =
          "ハウスカード番号をクリアする場合、会員情報のハウスカード番号もクリアされます。";
        return;
      }
    } else {
      house_card_change.value = false;
    }
  };

  const onClickUpdateCancel = async (): Promise<void> => {
    if (memberNotesBefore.value != "" && editDeliveryRequest.value) {
      editDeliveryRequest.value.member_notes = memberNotesBefore.value;
      memberNotesBefore.value = "";
      messageMemberNotes.value = "";
    }
    if (deliveryNotesBefore.value != "" && editDeliveryRequest.value) {
      editDeliveryRequest.value.delivery_notes = deliveryNotesBefore.value;
      deliveryNotesBefore.value = "";
      messageDeliveryNotes.value = "";
    }

    editDeliveryRequest.value = null;
    editPage.value = false;
    confirmPage.value = false;

    // 一覧に戻る際、保存したページ位置で再取得する
    await returnToListPage();
  };

  const onClickUpdate = async (): Promise<void> => {
    processFlag.value = 1;
    editPage.value = false;
    confirmPage.value = true;

    if (editDeliveryRequest.value?.member_notes?.includes('"')) {
      memberNotesBefore.value = editDeliveryRequest.value.member_notes;
      editDeliveryRequest.value.member_notes =
        editDeliveryRequest.value.member_notes.replace(/"/g, "");
      messageMemberNotes.value =
        "(※会員備考のダブルクォーテーションは削除されています)";
    }
    if (editDeliveryRequest.value?.delivery_notes?.includes('"')) {
      deliveryNotesBefore.value = editDeliveryRequest.value.delivery_notes;
      editDeliveryRequest.value.delivery_notes =
        editDeliveryRequest.value.delivery_notes.replace(/"/g, "");
      messageDeliveryNotes.value =
        "(※配達備考のダブルクォーテーションは削除されています)";
    }

    if (editDeliveryRequest.value!.delivery_id === 0) {
      if (irregularDeliveryStartTime.value.length === 0) {
        irregularDeliveryStartTime.value =
          editDeliveryRequest.value!.irregular_delivery_start_time!.substring(
            0,
            editDeliveryRequest.value!.irregular_delivery_start_time!.length - 3
          );
        irregularDeliveryEndTime.value =
          editDeliveryRequest.value!.irregular_delivery_end_time!.substring(
            0,
            editDeliveryRequest.value!.irregular_delivery_end_time!.length - 3
          );
      }
    }

    isDoubleDeli.value = false;
    isCharterDeli.value = false;
    doubleDeliOriconCount.value = getDoubleDeliOriconCount();
    charterDeliOriconCount.value = getCharterDeliOriconCount();
    if (
      doubleDeliOriconCount.value !== null &&
      editDeliveryRequest.value!.unit >= doubleDeliOriconCount.value! &&
      editDeliveryRequest.value!.unit < charterDeliOriconCount.value!
    ) {
      isDoubleDeli.value = true;
    } else if (
      charterDeliOriconCount.value !== null &&
      editDeliveryRequest.value!.unit >= charterDeliOriconCount.value!
    ) {
      isCharterDeli.value = true;
    }
  };

  const onClickConfirmUpdateCancel = async (): Promise<void> => {
    if (processFlag.value < 2) {
      editPage.value = true;
      confirmPage.value = false;
    } else {
      editPage.value = false;
      confirmPage.value = false;
    }
  };

  const updateDeliveryRequest = async (): Promise<void> => {
    if (!editDeliveryRequest.value) {
      return;
    }

    if (deliveryId_0_Flg.value) {
      // 都度便は開始/終了時刻が確定していない状態では更新しない。
      if (
        irregularDeliveryStartTime.value.length === 0 ||
        irregularDeliveryEndTime.value.length === 0
      ) {
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、時・分ともに入力してください。";
        return;
      }
      editDeliveryRequest.value.irregular_delivery_start_time =
        irregularDeliveryStartTime.value + ":00";
      editDeliveryRequest.value.irregular_delivery_end_time =
        irregularDeliveryEndTime.value + ":00";
    } else {
      editDeliveryRequest.value.irregular_delivery_start_time = "";
      editDeliveryRequest.value.irregular_delivery_end_time = "";
    }

    if (
      editDeliveryRequest.value.register_no != null &&
      typeof editDeliveryRequest.value.register_no === "string" &&
      editDeliveryRequest.value.register_no === ""
    ) {
      editDeliveryRequest.value.register_no = null;
    }

    if (
      editDeliveryRequest.value.receipt_no != null &&
      typeof editDeliveryRequest.value.receipt_no === "string" &&
      editDeliveryRequest.value.receipt_no === ""
    ) {
      editDeliveryRequest.value.receipt_no = null;
    }

    editDeliveryRequest.value.pre_double_deli_oricon_flg =
      pre_double_deli_oricon_flg.value;
    editDeliveryRequest.value.pre_charter_deli_oricon_flg =
      pre_charter_deli_oricon_flg.value;
    editDeliveryRequest.value.double_deli_oricon_flg = isDoubleDeli.value ? 1 : 0;
    editDeliveryRequest.value.charter_deli_oricon_flg = isCharterDeli.value ? 1 : 0;

    const apolloClient = await generateApolloClient();

    const registerNoRaw = editDeliveryRequest.value.register_no as
      | string
      | number
      | null
      | undefined;
    const receiptNoRaw = editDeliveryRequest.value.receipt_no as
      | string
      | number
      | null
      | undefined;
    const normalizeOptionalNumber = (
      value: string | number | null | undefined
    ): number | null | undefined => {
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
          return null;
        }
        const num = Number(trimmed);
        return Number.isNaN(num) ? undefined : num;
      }
      return value;
    };

    const input: DeliveryRequestUpdateInput = {
      area_code: editDeliveryRequest.value.area_code,
      chain_code: editDeliveryRequest.value.chain_code,
      charter_deli_oricon_flg: editDeliveryRequest.value.charter_deli_oricon_flg,
      customer_code: editDeliveryRequest.value.customer_code,
      delivery_address1: editDeliveryRequest.value.delivery_address1,
      delivery_address2: editDeliveryRequest.value.delivery_address2,
      delivery_address3: editDeliveryRequest.value.delivery_address3,
      delivery_id: editDeliveryRequest.value.delivery_id,
      delivery_name1: editDeliveryRequest.value.delivery_name1,
      delivery_name2: editDeliveryRequest.value.delivery_name2,
      delivery_name_kana1: editDeliveryRequest.value.delivery_name_kana1,
      delivery_name_kana2: editDeliveryRequest.value.delivery_name_kana2,
      delivery_notes: editDeliveryRequest.value.delivery_notes,
      delivery_phone: editDeliveryRequest.value.delivery_phone,
      delivery_zip_code: editDeliveryRequest.value.delivery_zip_code,
      double_deli_oricon_flg: editDeliveryRequest.value.double_deli_oricon_flg,
      house_card: editDeliveryRequest.value.house_card,
      irregular_delivery_end_time: editDeliveryRequest.value.irregular_delivery_end_time,
      irregular_delivery_start_time: editDeliveryRequest.value.irregular_delivery_start_time,
      member_id: editDeliveryRequest.value.member_id,
      member_notes: editDeliveryRequest.value.member_notes,
      payment_amount: parseInt(dummy_payment_amount.value.replace(/,/g, "")),
      payment_method: editDeliveryRequest.value.payment_method,
      pre_charter_deli_oricon_flg: editDeliveryRequest.value.pre_charter_deli_oricon_flg,
      pre_double_deli_oricon_flg: editDeliveryRequest.value.pre_double_deli_oricon_flg,
      payment_only_edit: isDisabled.value,
      receipt_no: normalizeOptionalNumber(receiptNoRaw),
      reception_no: editDeliveryRequest.value.reception_no,
      register_no: normalizeOptionalNumber(registerNoRaw),
      required_delivery_date: editDeliveryRequest.value.required_delivery_date,
      shop_code: editDeliveryRequest.value.shop_code,
      unit: editDeliveryRequest.value.unit,
    };

    try {
      const result: FetchResult<UpdateDeliveryRequestMutation> =
        await apolloClient.mutate<
          UpdateDeliveryRequestMutation,
          UpdateDeliveryRequestMutationVariables
        >({
          mutation: UpdateDeliveryRequestDocument,
          variables: {
            input: input,
            logInput: operationInput.value,
          },
        });

      updateDeliveryRequestMutation.value = result.data;
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      // サーバ（締め日バリデーション等）が返したエラーメッセージがあれば優先して表示する
      const serverMessage =
        err instanceof ApolloError
          ? err.graphQLErrors[0]?.message
          : undefined;
      errMessage.value = serverMessage ?? "配達依頼の更新に失敗しました。";
      return;
    }

    const nowDate = new Date();
    const strNowDate =
      String(nowDate.getFullYear()) +
      "-" +
      String(nowDate.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(nowDate.getDate()).padStart(2, "0");
    const acceptableEndTimeAry = acceptableEndTime.value.split(":");
    if (
      deliveryId_0_Flg.value &&
      editDeliveryRequest.value.required_delivery_date == strNowDate &&
      (Number(acceptableEndTimeAry[0]) < nowDate.getHours() ||
        (Number(acceptableEndTimeAry[0]) == nowDate.getHours() &&
          Number(acceptableEndTimeAry[1]) < nowDate.getMinutes()))
    ) {
      overDeliveryEndTime.value = true;
      overlay.value = false;
      errdialog.value = true;
      errMessage.value = wordList["lbl_delivery_time_chk"];
    }
  };

  const createMember = async (): Promise<void> => {
    if (editDeliveryRequest.value === null) {
      return;
    }

    memberCreateInput.value = {
      ...memberCreateInput.value,
      address1: editDeliveryRequest.value.delivery_address1,
      address2: editDeliveryRequest.value.delivery_address2,
      address3: editDeliveryRequest.value.delivery_address3,
      chain_code: editDeliveryRequest.value.chain_code,
      customer_code: editDeliveryRequest.value.customer_code,
      name1: editDeliveryRequest.value.delivery_name1,
      name2: editDeliveryRequest.value.delivery_name2,
      name_kana1: editDeliveryRequest.value.delivery_name_kana1,
      name_kana2: editDeliveryRequest.value.delivery_name_kana2,
      notes: editDeliveryRequest.value.member_notes,
      phone: editDeliveryRequest.value.delivery_phone,
      shop_code: editDeliveryRequest.value.shop_code,
      zip_code: editDeliveryRequest.value.delivery_zip_code,
      house_card: editDeliveryRequest.value.house_card,
    };

    const apolloClient = await generateApolloClient();

    const result: FetchResult<CreateMemberMutation> = await apolloClient.mutate<
      CreateMemberMutation,
      CreateMemberMutationVariables
    >({
      mutation: CreateMemberDocument,
      variables: {
        input: memberCreateInput.value,
        logInput: operationInput.value,
      },
    });

    createMemberMutation.value = result.data;
    if (
      createMemberMutation.value !== null &&
      createMemberMutation.value !== undefined
    ) {
      editDeliveryRequest.value.member_id = createMemberMutation.value?.createMember
        ?.member_id
        ? Number(createMemberMutation.value?.createMember?.member_id)
        : undefined;
    }
  };

  const updateMember = async (): Promise<void> => {
    if (editDeliveryRequest.value === null) {
      return;
    }

    memberUpdateInput.value = {
      ...memberUpdateInput.value,
      customer_code: editDeliveryRequest.value.customer_code,
      chain_code: editDeliveryRequest.value.chain_code,
      address1: editDeliveryRequest.value.delivery_address1,
      address2: editDeliveryRequest.value.delivery_address2,
      address3: editDeliveryRequest.value.delivery_address3,
      member_id: String(editDeliveryRequest.value.member_id),
      name1: editDeliveryRequest.value.delivery_name1,
      name2: editDeliveryRequest.value.delivery_name2,
      name_kana1: editDeliveryRequest.value.delivery_name_kana1,
      name_kana2: editDeliveryRequest.value.delivery_name_kana2,
      search_kana:
        editDeliveryRequest.value.delivery_name_kana1 +
        editDeliveryRequest.value.delivery_name_kana2,
      notes: editDeliveryRequest.value.member_notes,
      phone: editDeliveryRequest.value.delivery_phone,
      shop_code: editDeliveryRequest.value.shop_code,
      zip_code: editDeliveryRequest.value.delivery_zip_code,
      house_card: editDeliveryRequest.value.house_card!,
    };

    const apolloClient = await generateApolloClient();

    const result: FetchResult<UpdateMemberMutation> = await apolloClient.mutate<
      UpdateMemberMutation,
      UpdateMemberMutationVariables
    >({
      mutation: UpdateMemberDocument,
      variables: {
        input: memberUpdateInput.value,
        logInput: operationInput.value,
      },
    });

    updateMemberMutation.value = result.data;
    if (
      updateMemberMutation.value !== null &&
      updateMemberMutation.value !== undefined
    ) {
      editDeliveryRequest.value.member_id = updateMemberMutation.value?.updateMember
        ?.member_id
        ? Number(updateMemberMutation.value?.updateMember?.member_id)
        : undefined;
    }
  };

  const onClickConfirmUpdateOk = async (): Promise<void> => {
    overlay.value = true;

    try {
      await updateDeliveryRequest();

      editDeliveryRequest.value = null;
      openListPage();
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      errMessage.value = "配達依頼の登録に失敗しました。";
      return;
    }

    // 更新確定後に元ページへ戻って再取得する
    await returnToListPage();
    overlay.value = false;
  };

  const onClickConfirmUpdateAndMember = async (): Promise<void> => {
    overlay.value = true;
    try {
      if (
        editDeliveryRequest.value?.member_id == null ||
        editDeliveryRequest.value?.member_id == undefined ||
        editDeliveryRequest.value?.member_id == 0
      ) {
        await createMember();
      } else {
        await updateMember();
      }

      await updateDeliveryRequest();

      editDeliveryRequest.value = null;
      openListPage();
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      errMessage.value = "配達依頼の登録に失敗しました。";
      return;
    }
    await returnToListPage();
    overlay.value = false;
  };

  return {
    editFormRef,
    validateEditForm,
    onChangePaymentMethod,
    commmaMoney,
    onFocus,
    onBlur,
    onChangeHouseCard,
    onClickUpdateCancel,
    onClickUpdate,
    onClickConfirmUpdateCancel,
    onClickConfirmUpdateOk,
    onClickConfirmUpdateAndMember,
  };
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestEditState = () => {
  const editDeliveryRequest = ref<DeliveryRequestUpdateInput | null>(null);
  const paymentMethodItems = ref<(string | undefined)[]>([]);
  const dummy_payment_amount = ref("");

  return {
    editDeliveryRequest,
    paymentMethodItems,
    dummy_payment_amount,
  };
};
